import {
  Avatar,
  Collapse,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  SvgIcon,
  Typography,
} from '@mui/material';
import {
  FunctionComponent,
  Fragment,
  useState,
  useMemo,
  useEffect,
  ReactNode,
  forwardRef,
  ForwardedRef,
  PropsWithChildren,
} from 'react';
import {
  Link as RouterLink,
  LinkProps as RouterLinkProps,
  useLocation,
} from 'react-router-dom';

import Icons from '@Constants/Icons.constants';
import { selectAuthState } from '@Selectors/auth.selector';
import { useAppSelector } from '@Utils/hooks/useStore.hook';

import { StyledList } from './SideBarMenu.styles';
import {
  SideBarMenuItem,
  SideBarMenuProps,
  ListItemLinkProps,
} from './SideBarMenu.types';

/** Router Link Component */
const ListItemLink: FunctionComponent<ListItemLinkProps> = (
  props: ListItemLinkProps
): JSX.Element => {
  const { icon, label, to, toParams, isNestedSecondLevel } = props;
  const location = useLocation();
  const renderLink = useMemo(
    () =>
      forwardRef<HTMLAnchorElement, Omit<RouterLinkProps, 'to'>>(
        Object.assign(
          (
            itemProps: PropsWithChildren<Omit<RouterLinkProps<unknown>, 'to'>>,
            ref: ForwardedRef<HTMLAnchorElement>
          ) => (
            <RouterLink
              to={`${to}${toParams}`}
              ref={ref}
              {...itemProps}
              role={undefined}
            />
          ),
          { displayName: 'routerLinkRefFunc' }
        )
      ),
    [to, toParams]
  );

  return (
    <ListItemButton component={renderLink} selected={location.pathname === to}>
      {icon && (
        <ListItemIcon>
          <SvgIcon>{Icons[icon]}</SvgIcon>
        </ListItemIcon>
      )}
      <ListItemText disableTypography>
        <Typography
          variant="input"
          fontWeight="bold"
          pl={isNestedSecondLevel ? 3 : 0}
        >
          {label}
        </Typography>
      </ListItemText>
    </ListItemButton>
  );
};

/** Menu Component */
const SideBarMenu: FunctionComponent<SideBarMenuProps> = (
  props: SideBarMenuProps
): JSX.Element => {
  const { items, logo } = props;
  const location = useLocation();

  /** States */
  const [open, setOpen] = useState<{ [key: string]: boolean }>({});

  const userRole = useAppSelector(selectAuthState)?.role as string;

  /** Returns id for menuItems to be used for maintaining open/close state */
  const getId = (parent: string, label: string): string =>
    `${parent}-${label.toLowerCase().replaceAll(' ', '-')}`;
  useEffect(() => {
    /**
     * Sets the initial state for SideMenu Collapsible Items
     *
     * Opens if the route matches else leave it as closed.
     */
    const getSideMenuOpenInitialState = (
      initialItems: SideBarMenuItem[],
      parent: string
    ) => {
      initialItems.forEach((item) => {
        if (
          item.children &&
          location.pathname.includes(
            item.label.toLowerCase().replaceAll(' ', '-')
          )
        ) {
          const key = getId(parent, item.label);
          setOpen((prevState) => ({ ...prevState, [key]: true }));
          getSideMenuOpenInitialState(item.children, key);
        }
      });
    };

    getSideMenuOpenInitialState(items, 'root');
  }, [items, location.pathname]);

  /**
   * Menu Item Dropdown Click Handler
   *
   * if `open` - closes it and it's nested levels
   * else opens only the parent dropdown
   */
  const handleClick = (id: string) => {
    if (open[id]) {
      Object.keys(open).forEach((key) => {
        if (key.includes(id)) {
          setOpen((prevState) => ({
            ...prevState,
            [key]: false,
          }));
        }
      });
    } else {
      setOpen((prevState) => ({
        ...prevState,
        [id]: true,
      }));
    }
  };

  /** Dropdown button selected state if any children is selected */
  const isSelected = (childItems?: SideBarMenuItem[]) => {
    let flag = false;
    childItems?.forEach((childItem: SideBarMenuItem) => {
      if (childItem.to === location.pathname) {
        flag = true;
        return flag;
      }
    });
    return flag;
  };

  /** Recursive Function
   *
   * Renders the multi-level nesting Menu
   */
  const renderNestedMenu = (
    nestedMenuItems: SideBarMenuItem[],
    parent: string
  ): ReactNode =>
    nestedMenuItems.map((item: SideBarMenuItem, index: number) => {
      const {
        label,
        icon,
        to,
        toParams,
        children,
        accessRights = [],
        isNestedSecondLevel,
      } = item;

      /** If children is present - renders the Dropdown Button */
      if (children) {
        return accessRights?.includes(userRole) ? (
          <Fragment key={index}>
            <ListItemButton
              selected={isSelected(children)}
              onClick={() => handleClick(getId(parent, label))}
            >
              {icon && (
                <ListItemIcon>
                  <SvgIcon>{Icons[icon]}</SvgIcon>
                </ListItemIcon>
              )}
              <ListItemText disableTypography>
                <Typography variant="input" fontWeight="bold">
                  {label}
                </Typography>
              </ListItemText>
              <SvgIcon>
                {open[getId(parent, label)]
                  ? Icons.expandMore
                  : Icons.keyboardArrowRight}
              </SvgIcon>
            </ListItemButton>
            <Collapse
              in={open[getId(parent, label)]}
              unmountOnExit
              timeout="auto"
            >
              <StyledList>
                {renderNestedMenu(children, getId(parent, label))}
              </StyledList>
            </Collapse>
          </Fragment>
        ) : null;
      }

      /** If children is not present renders the Router Link */
      // check if the user role is present in the accessRights array
      return accessRights?.includes(userRole) ? (
        <ListItemLink
          key={index}
          icon={icon}
          label={label}
          to={to || ''}
          toParams={toParams || ''}
          isNestedSecondLevel={isNestedSecondLevel}
        />
      ) : null;
    });

  return (
    <>
      <Avatar
        variant="square"
        src={logo}
        sx={{
          width: '100%',
          height: 'auto',
          maxWidth: 202,
          maxHeight: 30,
          mx: 'auto',
          my: 2,
        }}
      />
      <StyledList>{renderNestedMenu(items, 'root')}</StyledList>;
    </>
  );
};

export default SideBarMenu;
